import { Button } from "@/components/ui/button"
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "@/components/ui/resizable"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { AlertCircle, ArrowRight, Check, CheckCircle2 } from "lucide-react"
import { useState } from "react"
import {
  useReconciliation,
  useResolveReconciliation,
} from "../../../api/questionnaireProcessingApi"
import { FooterActionBar } from "../../shared/FooterActionBar"
import { McPill } from "../../shared/McPill"
import { ModuleHeader } from "../../shared/ModuleHeader"
import { ArticlePicker } from "./ArticlePicker"
import { useProcessingOutletContext } from "./QuestionnaireProcessingLayout"
import { SheetPreviewPanel } from "./SheetPreviewPanel"

type ReconAction = "accept" | "change" | "skip"
type ReconResolveItem = {
  itemId: string
  action: ReconAction
  articleId?: string | null
}

export function ReconcileStep() {
  const { translate } = useTranslation()
  const { questionnaireId } = useProcessingOutletContext()
  const { data, isLoading } = useReconciliation(questionnaireId)
  const { mutate: resolve, isPending } = useResolveReconciliation()
  const [decisions, setDecisions] = useState<Record<string, ReconAction>>({})
  const [changeSelections, setChangeSelections] = useState<
    Record<string, { articleId: string; articleNumber: string }>
  >({})
  const [activePart, setActivePart] = useState<string | null>(null)

  const items = data?.items ?? []
  const catalogueEmpty = data?.catalogueEmpty ?? false
  const needsReview = items.filter((i) => i.status !== "matched")
  const matchedAuto = items.filter((i) => i.status === "matched")
  const allResolved = needsReview.every(
    (i) => decisions[i.itemId] || catalogueEmpty || i.noCandidates,
  )
  const unmappedNoCandidates = needsReview.filter(
    (i) => i.noCandidates && !decisions[i.itemId],
  ).length

  const handlePick =
    (itemId: string) => (articleId: string, articleNumber: string) => {
      setChangeSelections((prev) => ({
        ...prev,
        [itemId]: { articleId, articleNumber },
      }))
      setDecisions((prev) => ({ ...prev, [itemId]: "change" }))
    }

  if (isLoading || !data) {
    return <div className="flex h-full items-center justify-center" />
  }

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden">
      <div className="px-6 pb-2 pt-1">
        <ModuleHeader
          title={translate("MC_QP_RECON_TITLE")}
          helpArticle={{
            categoryId: "customer-studio",
            articleId: "customer.requests",
          }}
          subtitle={translate("MC_QP_RECON_SUBTITLE", {
            matched: data.matched,
            needsReview: data.needsReview,
          })}
        />
      </div>

      {catalogueEmpty && (
        <div className="mx-6 mb-2 rounded-md border border-warning/25 bg-warning/6 px-3.5 py-2.5 text-[12px] text-warning">
          {translate("MC_QP_RECON_CATALOGUE_EMPTY")}
        </div>
      )}

      {!catalogueEmpty && unmappedNoCandidates > 0 && (
        <div className="mx-6 mb-2 rounded-md border border-warning/25 bg-warning/6 px-3.5 py-2.5 text-[12px] text-warning">
          {translate("MC_QP_RECON_NO_CANDIDATES_WARNING", {
            n: unmappedNoCandidates,
          })}
        </div>
      )}

      <div className="min-h-0 flex-1 px-6 py-4 pb-0">
        <div className="h-full overflow-hidden rounded-lg border">
          <ResizablePanelGroup orientation="horizontal">
            <ResizablePanel defaultSize={42} minSize={28}>
              <div className="flex h-full min-h-0 flex-col overflow-y-auto">
                {needsReview.length > 0 && (
                  <div className="flex items-center gap-1.5 border-b px-4 py-2">
                    <AlertCircle size={12} className="text-warning" />
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-warning">
                      {translate("MC_QP_RECON_NEEDS_CONFIRMATION")}
                    </p>
                  </div>
                )}
                {needsReview.map((item) => {
                  const decision = decisions[item.itemId]
                  const candidate = item.candidates[0]
                  const changeSelection = changeSelections[item.itemId]
                  const autoSkipped =
                    (catalogueEmpty || item.noCandidates) &&
                    !decisions[item.itemId]
                  const canChange = !catalogueEmpty

                  const confirmedArticleNumber =
                    decision === "change"
                      ? changeSelection?.articleNumber
                      : decision === "accept"
                        ? (candidate?.articleNumber ?? item.articleNumber)
                        : undefined
                  const isConfirmed = !!confirmedArticleNumber

                  const needsSelection =
                    !isConfirmed && (autoSkipped || decision === "skip")

                  return (
                    <div
                      key={item.itemId}
                      onClick={() => setActivePart(item.customerArticleNumber)}
                      className="cursor-pointer border-b px-4 py-3 hover:bg-primary/4"
                    >
                      {needsSelection ? (
                        <>
                          <div className="mb-1 flex items-center gap-2">
                            <span className="font-mono text-[12px] text-foreground">
                              {item.customerArticleNumber}
                            </span>
                            <span className="text-[12px] text-muted-foreground">
                              →
                            </span>
                            {canChange ? (
                              <ArticlePicker
                                triggerLabel={translate(
                                  "MC_QP_RECON_SELECT_ARTICLE",
                                )}
                                onSelect={handlePick(item.itemId)}
                              />
                            ) : (
                              <McPill tone="muted">
                                {translate("MC_QP_RECON_NO_MATCH")}
                              </McPill>
                            )}
                          </div>
                          {decision === "skip" && (
                            <McPill tone="muted">
                              {translate("MC_QP_RECON_SKIPPED")}
                            </McPill>
                          )}
                        </>
                      ) : isConfirmed ? (
                        <>
                          <div className="mb-1 flex items-center gap-2">
                            <span className="font-mono text-[12px] text-foreground">
                              {item.customerArticleNumber}
                            </span>
                            <span className="text-[12px] text-muted-foreground">
                              →
                            </span>
                            <span className="font-mono truncate text-[12px] text-foreground">
                              {confirmedArticleNumber}
                            </span>
                            {canChange && (
                              <ArticlePicker
                                variant="icon"
                                triggerLabel={translate("MC_QP_RECON_CHANGE")}
                                onSelect={handlePick(item.itemId)}
                              />
                            )}
                          </div>
                          <McPill tone="success" icon={Check}>
                            {translate("MC_QP_RECON_CONFIRMED_LABEL")}
                          </McPill>
                        </>
                      ) : (
                        <>
                          <div className="mb-1 flex items-center justify-between gap-2">
                            <span className="font-mono text-[12px] text-foreground">
                              {item.customerArticleNumber}
                            </span>
                            {candidate && (
                              <McPill tone="doubt">{`${Math.round(candidate.confidence * 100)}%`}</McPill>
                            )}
                          </div>
                          {candidate && (
                            <p className="mb-2 truncate text-[11px] text-muted-foreground">
                              → {candidate.articleNumber} · {candidate.name}
                            </p>
                          )}
                          <div className="flex flex-wrap gap-2">
                            {candidate && (
                              <Button
                                size="sm"
                                className="h-7 bg-yellow1 px-2 text-[11px] text-black hover:bg-yellow2"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  setDecisions((prev) => ({
                                    ...prev,
                                    [item.itemId]: "accept",
                                  }))
                                }}
                              >
                                {translate("MC_QP_RECON_ACCEPT")}
                              </Button>
                            )}
                            {canChange && (
                              <ArticlePicker
                                triggerLabel={translate("MC_QP_RECON_CHANGE")}
                                onSelect={handlePick(item.itemId)}
                              />
                            )}
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 px-2 text-[11px]"
                              onClick={(e) => {
                                e.stopPropagation()
                                setDecisions((prev) => ({
                                  ...prev,
                                  [item.itemId]: "skip",
                                }))
                              }}
                            >
                              {translate("MC_QP_RECON_SKIP")}
                            </Button>
                          </div>
                        </>
                      )}
                    </div>
                  )
                })}

                {matchedAuto.length > 0 && (
                  <div className="flex items-center gap-1.5 border-b px-4 py-2">
                    <CheckCircle2 size={12} className="text-success" />
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-success">
                      {translate("MC_QP_RECON_MATCHED_AUTO", {
                        count: matchedAuto.length,
                      })}
                    </p>
                  </div>
                )}
                {matchedAuto.map((item) => {
                  const changeSelection = changeSelections[item.itemId]
                  const candidate = item.candidates[0]
                  const displayArticleNumber =
                    changeSelection?.articleNumber ??
                    item.articleNumber ??
                    item.customerArticleNumber
                  const displayName = changeSelection
                    ? undefined
                    : (item.articleName ?? candidate?.name)

                  return (
                    <div
                      key={item.itemId}
                      onClick={() => setActivePart(item.customerArticleNumber)}
                      className="cursor-pointer border-b px-4 py-3 hover:bg-primary/4"
                    >
                      <div className="mb-1 flex items-center gap-2">
                        <span className="font-mono text-[12px] text-foreground">
                          {item.customerArticleNumber}
                        </span>
                        <span className="text-[12px] text-muted-foreground">
                          →
                        </span>
                        <span className="font-mono truncate text-[12px] text-foreground">
                          {displayArticleNumber}
                        </span>
                        <ArticlePicker
                          variant="icon"
                          triggerLabel={translate("MC_QP_RECON_CHANGE")}
                          onSelect={handlePick(item.itemId)}
                        />
                      </div>
                      {displayName && (
                        <p className="mb-2 truncate text-[11px] text-muted-foreground">
                          {displayName}
                        </p>
                      )}
                      <McPill tone="success" icon={Check}>
                        {translate("MC_QP_RECON_CONFIRMED_LABEL")}
                      </McPill>
                    </div>
                  )
                })}
              </div>
            </ResizablePanel>
            <ResizableHandle withHandle />
            <ResizablePanel defaultSize={58} minSize={30}>
              <SheetPreviewPanel
                questionnaireId={questionnaireId}
                activeSheetName="PN_List"
                highlightHeader="Referenced PN"
                highlightArticleNumber={activePart}
              />
            </ResizablePanel>
          </ResizablePanelGroup>
        </div>
      </div>

      <FooterActionBar>
        <Button
          data-agent-target="request.confirm-matches"
          className="bg-yellow1 text-black hover:bg-yellow2"
          disabled={!allResolved || isPending}
          onClick={() =>
            resolve({
              id: questionnaireId,
              body: {
                items: [
                  ...needsReview.map((item): ReconResolveItem => {
                    const action = decisions[item.itemId] ?? "skip"
                    if (action === "change") {
                      const articleId = changeSelections[item.itemId]?.articleId
                      // No manual selection made — nothing valid to send, so skip
                      // this part rather than risk a 422 from a missing articleId.
                      if (!articleId)
                        return { itemId: item.itemId, action: "skip" }
                      return { itemId: item.itemId, action, articleId }
                    }
                    return { itemId: item.itemId, action }
                  }),
                  ...matchedAuto
                    .filter((item) => changeSelections[item.itemId]?.articleId)
                    .map(
                      (item): ReconResolveItem => ({
                        itemId: item.itemId,
                        action: "change",
                        articleId: changeSelections[item.itemId]!.articleId,
                      }),
                    ),
                ],
              },
            })
          }
        >
          {translate("MC_QP_RECON_CONTINUE_BUTTON")}
          <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </FooterActionBar>
    </div>
  )
}
